import React, { useState, useEffect } from "react";
import { getSavedShorts, updateShort } from "../../services/shortService";
import Swal from "sweetalert2";
import { useSearchParams, useLocation } from "react-router-dom";

const SavedShorts = ({ collapsed }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [pagination, setPagination] = useState({
    pageSize: Number(searchParams.get("pageSize")) || 12,
    page: Number(searchParams.get("page")) || 1,
    totalItems: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    filter: searchParams.get("filter") || "all",
  });

  const openModal = (item) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await updateShort(editingItem.short_id, editTitle);

      const updated = items.map((i) =>
        i.short_id === editingItem.short_id ? { ...i, title: editTitle } : i,
      );

      setItems(updated);

      Swal.fire({
        icon: "success",
        title: "Saved!",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Update failed",
      });
    } finally {
      setIsSaving(false);
      setIsModalOpen(false);
    }
  };

  const fetchData = async ({
    page = pagination.page,
    pageSize = pagination.pageSize,
    filter = pagination.filter,
  } = {}) => {
    setIsLoading(true);

    try {
      const query = new URLSearchParams({
        page,
        pageSize,
        filter,
      });

      const res = await getSavedShorts(query);

      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePageChange = (page) => {
    fetchData({ page });
  };

  const handlePageSizeChange = (e) => {
    fetchData({ page: 1, pageSize: parseInt(e.target.value) });
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;

    const params = new URLSearchParams(window.location.search);
    params.set("filter", value);

    window.history.replaceState(
      {},
      "",
      `${location.pathname}?${params.toString()}`,
    );

    fetchData({ page: 1, filter: value });
  };

  return (
    <div
      className={`transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-72"} p-6`}
    >
      {/* HEADER */}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Generated Shorts
        </h1>

        <div className="text-sm text-gray-500">
          {pagination.totalItems} Shorts
        </div>
      </div>

      {/* CONTROLS */}

      <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-2">
          <label className="text-sm">Items per page</label>

          <select
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            className="border px-2 py-1 rounded"
          >
            <option value="6">6</option>
            <option value="12">12</option>
            <option value="24">24</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm">Filter</label>

          <select
            value={pagination.filter}
            onChange={handleFilterChange}
            className="border px-2 py-1 rounded"
          >
            <option value="new">Newly Generated</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* LOADER */}

      {isLoading && (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* SHORTS GRID */}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {items.map((item) => (
          <div
            key={item.short_id}
            className="bg-white rounded-xl shadow hover:shadow-xl transition overflow-hidden"
          >
            {/* VIDEO */}

            <div className="relative">
              <video
                src={item.video_url}
                muted
                className="w-full h-[420px] object-cover bg-black"
                onMouseEnter={(e) => e.target.play()}
                onMouseLeave={(e) => {
                  e.target.pause();
                  e.target.currentTime = 0;
                }}
              />

              <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {item.duration}s
              </span>
            </div>

            {/* INFO */}

            <div className="p-4">
              <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                {item.title}
              </p>

              <p className="text-xs text-gray-500 mt-1">
                {item.generation_time}
              </p>
            </div>

            {/* ACTIONS */}

            <div className="flex justify-between px-4 pb-4">
              <button
                onClick={() => openModal(item)}
                className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
              >
                Edit
              </button>

              <a
                href={item.video_url}
                download
                className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Download
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}

      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            disabled={!pagination.hasPrevious}
            onClick={() => handlePageChange(pagination.page - 1)}
            className="px-4 py-1 border rounded"
          >
            Previous
          </button>

          <span className="text-sm">
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            disabled={!pagination.hasNext}
            onClick={() => handlePageChange(pagination.page + 1)}
            className="px-4 py-1 border rounded"
          >
            Next
          </button>
        </div>
      )}

      {/* EDIT MODAL */}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[420px]">
            <h2 className="text-lg font-semibold mb-4">Edit Short Title</h2>

            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full border p-2 rounded mb-4"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="border px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedShorts;
